'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { InteractionPlatformAdapters, InteractionStorageAdapter } from '../../model/interaction/adapters';
import {
  INTERACTION_STORAGE_KEYS as STORAGE_KEYS,
  TIMER_DURATION_SECONDS,
  TIMER_PERSIST_INTERVAL_MS,
} from '../../constants/interaction/constants';

interface TimerState {
  /** Accumulated elapsed time in seconds (excluding current run) */
  elapsed: number;
}

export interface UseAccurateTimerReturn {
  /** Progress from 0 to 1 */
  progress: number;
  /** Whether timer is actively counting */
  isRunning: boolean;
  /** Seconds remaining on the countdown */
  remainingSeconds: number;
  /** Whether the timer has completed */
  isComplete: boolean;
  /** Load state from localStorage without starting the timer */
  initialize: () => void;
  /** Pause the timer */
  pause: () => void;
  /** Resume the timer (start RAF loop) */
  resume: () => void;
  /** Reset timer to initial state (for next reward cycle) */
  reset: () => void;
}

const DEFAULT_TIMER_STATE: TimerState = { elapsed: 0 };

function loadTimerState(storage: InteractionStorageAdapter): TimerState {
  return storage.getItem<TimerState>(STORAGE_KEYS.timer, DEFAULT_TIMER_STATE);
}

function saveTimerState(storage: InteractionStorageAdapter, state: TimerState): void {
  storage.setItem(STORAGE_KEYS.timer, state);
}

export interface UseAccurateTimerOptions {
  /** Called once when timer completes (not on every render while complete) */
  onComplete?: () => void;
}

export function useAccurateTimer(
  adapters: InteractionPlatformAdapters,
  options: UseAccurateTimerOptions = {},
): UseAccurateTimerReturn {
  const onCompleteRef = useRef(options.onComplete);
  onCompleteRef.current = options.onComplete;
  const storageRef = useRef(adapters.storage);
  storageRef.current = adapters.storage;

  const timingRef = useRef(adapters.timing);
  timingRef.current = adapters.timing;

  const progressRef = useRef(0);
  const remainingSecondsRef = useRef(TIMER_DURATION_SECONDS);
  const isCompleteRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(TIMER_DURATION_SECONDS);
  const [isComplete, setIsComplete] = useState(false);

  const runStartRef = useRef<number | null>(null);
  const accumulatedRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  const lastPersistRef = useRef(0);
  const initializedRef = useRef(false);

  const getTotalElapsed = useCallback(() => {
    const currentRunElapsed =
      runStartRef.current !== null
        ? (timingRef.current.now() - runStartRef.current) / 1000
        : 0;
    return accumulatedRef.current + currentRunElapsed;
  }, []);

  const computeProgress = useCallback(
    () => Math.min(getTotalElapsed() / TIMER_DURATION_SECONDS, 1),
    [getTotalElapsed],
  );

  const computeRemainingSeconds = useCallback(
    () => Math.max(0, Math.ceil(TIMER_DURATION_SECONDS - getTotalElapsed())),
    [getTotalElapsed],
  );

  const persistState = useCallback(() => {
    saveTimerState(storageRef.current, { elapsed: getTotalElapsed() });
  }, [getTotalElapsed]);

  const tick = useCallback(() => {
    const now = timingRef.current.now();
    const prog = computeProgress();
    progressRef.current = prog;

    const newRemaining = computeRemainingSeconds();
    if (newRemaining !== remainingSecondsRef.current) {
      remainingSecondsRef.current = newRemaining;
      setRemainingSeconds(newRemaining);
    }

    const complete = prog >= 1;
    const justCompleted = complete && !isCompleteRef.current;
    if (justCompleted) {
      isCompleteRef.current = true;
      setIsComplete(true);
      onCompleteRef.current?.();
    }

    if (now - lastPersistRef.current >= TIMER_PERSIST_INTERVAL_MS) {
      persistState();
      lastPersistRef.current = now;
    }

    if (!complete) {
      rafIdRef.current = timingRef.current.requestFrame(tick);
    } else {
      setIsRunning(false);
      accumulatedRef.current = getTotalElapsed();
      runStartRef.current = null;
      persistState();
    }
  }, [computeProgress, computeRemainingSeconds, persistState]);

  const initialize = useCallback(() => {
    if (initializedRef.current) {return;}
    initializedRef.current = true;

    const saved = loadTimerState(storageRef.current);
    accumulatedRef.current = saved.elapsed;

    if (saved.elapsed >= TIMER_DURATION_SECONDS) {
      progressRef.current = 1;
      remainingSecondsRef.current = 0;
      isCompleteRef.current = true;
      setRemainingSeconds(0);
      setIsComplete(true);
      return;
    }

    const prog = saved.elapsed / TIMER_DURATION_SECONDS;
    progressRef.current = prog;
    const remaining = Math.max(0, Math.ceil(TIMER_DURATION_SECONDS - saved.elapsed));
    remainingSecondsRef.current = remaining;
    setRemainingSeconds(remaining);
  }, []);

  const pause = useCallback(() => {
    if (rafIdRef.current !== null) {
      timingRef.current.cancelFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (runStartRef.current !== null) {
      const currentRunElapsed =
        (timingRef.current.now() - runStartRef.current) / 1000;
      accumulatedRef.current += currentRunElapsed;
      runStartRef.current = null;
    }

    setIsRunning(false);
    persistState();
  }, [persistState]);

  const resume = useCallback(() => {
    if (rafIdRef.current !== null) {return;}
    if (!initializedRef.current) {return;}

    if (accumulatedRef.current >= TIMER_DURATION_SECONDS) {
      progressRef.current = 1;
      remainingSecondsRef.current = 0;
      isCompleteRef.current = true;
      setRemainingSeconds(0);
      setIsComplete(true);
      return;
    }

    runStartRef.current = timingRef.current.now();
    lastPersistRef.current = timingRef.current.now();
    setIsRunning(true);
    rafIdRef.current = timingRef.current.requestFrame(tick);
  }, [tick]);

  const reset = useCallback(() => {
    if (rafIdRef.current !== null) {
      timingRef.current.cancelFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    runStartRef.current = null;
    accumulatedRef.current = 0;
    progressRef.current = 0;
    remainingSecondsRef.current = TIMER_DURATION_SECONDS;
    isCompleteRef.current = false;
    setIsRunning(false);
    setRemainingSeconds(TIMER_DURATION_SECONDS);
    setIsComplete(false);
    saveTimerState(storageRef.current, DEFAULT_TIMER_STATE);
  }, []);

  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;

  useEffect(() => {
    const unsubscribe = adapters.visibility.subscribe((state) => {
      if (state === 'hidden' && isRunningRef.current) {
        pause();
      }
    });
    return unsubscribe;
  }, [adapters.visibility, pause]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        timingRef.current.cancelFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (runStartRef.current !== null) {
        accumulatedRef.current +=
          (timingRef.current.now() - runStartRef.current) / 1000;
        runStartRef.current = null;
      }
      saveTimerState(storageRef.current, {
        elapsed: accumulatedRef.current,
      });
    };
  }, []);

  return {
    progress: progressRef.current,
    isRunning,
    remainingSeconds,
    isComplete,
    initialize,
    pause,
    resume,
    reset,
  };
}
